/**
 * LmsStandaloneQuizDetail — question editor + share links + analytics.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Link2, Copy, Trash2, Plus, BarChart3, ShieldAlert, Users, Sparkles } from "lucide-react";

const QTYPES = [["mcq", "Single choice"], ["multi", "Multi choice"], ["truefalse", "True / False"], ["short_text", "Short text"]];

export default function LmsStandaloneQuizDetail() {
  const { id } = useParams();
  const { activeWorkspaceId } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [tab, setTab] = useState("questions"); // questions | links | analytics
  const [links, setLinks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ kind: "single", label: "", expires_in_days: 30, require_otp: false, invitee_emails: "" });

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      // quiz meta comes from list — fetch questions + refresh via patch response
      const [qs, all] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}/questions`),
        api.get(`/workspaces/${activeWorkspaceId}/lms/quizzes/standalone`),
      ]);
      setQuestions(Array.isArray(qs.data) ? qs.data : []);
      const q = (all.data || []).find((x) => x.id === id);
      setQuiz(q);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId, id]);
  useEffect(() => { load(); }, [load]);

  const loadLinks = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}/share-links`);
      setLinks(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load links failed")); }
  };
  const loadAnalytics = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}/analytics`);
      setAnalytics(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load analytics failed")); }
  };
  useEffect(() => {
    if (tab === "links") loadLinks();
    if (tab === "analytics") loadAnalytics();
  }, [tab]); // eslint-disable-line

  const patchQuiz = async (body) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}`, body);
      setQuiz({ ...quiz, ...data });
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const addQuestion = async () => {
    try {
      const body = { type: "mcq", text: "New question", options: [{ key: "A", label: "Option A" }, { key: "B", label: "Option B" }], correct_answers: ["A"], points: 1 };
      await api.post(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}/questions`, body);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  const patchQuestion = async (qid, body) => {
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/lms/questions/${qid}`, body);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const deleteQuestion = async (qid) => {
    if (!confirm("Delete this question?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/lms/questions/${qid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const createShareLink = async () => {
    try {
      const payload = {
        kind: linkForm.kind,
        label: linkForm.label || null,
        expires_in_days: Number(linkForm.expires_in_days) || 30,
        require_otp: linkForm.require_otp,
      };
      if (linkForm.kind === "invited") {
        payload.invitee_emails = linkForm.invitee_emails
          .split(/[\s,;]+/).filter((x) => x.includes("@"));
        if (!payload.invitee_emails.length) return toast.error("Add at least one invitee email");
      }
      await api.post(`/workspaces/${activeWorkspaceId}/lms/quizzes/${id}/share-links`, payload);
      setLinkOpen(false);
      setLinkForm({ kind: "single", label: "", expires_in_days: 30, require_otp: false, invitee_emails: "" });
      loadLinks();
      toast.success("Share link created");
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  const revokeLink = async (linkId) => {
    if (!confirm("Revoke this link? Takers cannot use it after this.")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/lms/quiz-share-links/${linkId}`);
      loadLinks();
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };

  const publicBase = window.location.origin;
  const copyLink = (tok) => {
    const url = `${publicBase}/pq/${tok}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  if (!quiz) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={quiz.title}
        description={`Standalone quiz · ${quiz.prometric_mode} · ${quiz.question_count || 0} questions · ${quiz.attempt_count || 0} attempts`}
      />
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <aside className="border border-border p-3 space-y-3">
          <div>
            <Label className="text-[10px] label-eyebrow">Title</Label>
            <Input defaultValue={quiz.title} onBlur={(e) => e.target.value !== quiz.title && patchQuiz({ title: e.target.value })} className="h-8 rounded-none" data-testid="stdq-title" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Proctoring mode</Label>
            <Select value={quiz.prometric_mode || "strict"} onValueChange={(v) => patchQuiz({ prometric_mode: v })}>
              <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="strict">Strict</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="off">Off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Time limit (min)</Label>
            <Input type="number" min={1} defaultValue={quiz.time_limit_min || ""} onBlur={(e) => patchQuiz({ time_limit_min: e.target.value ? Number(e.target.value) : null })} className="h-8 rounded-none" placeholder="—" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Pass %</Label>
            <Input type="number" min={1} max={100} defaultValue={quiz.pass_percent || 70} onBlur={(e) => patchQuiz({ pass_percent: Number(e.target.value) })} className="h-8 rounded-none" />
          </div>
          <div>
            <Label className="text-[10px] label-eyebrow">Max attempts</Label>
            <Input type="number" min={1} defaultValue={quiz.max_attempts || 1} onBlur={(e) => patchQuiz({ max_attempts: Number(e.target.value) })} className="h-8 rounded-none" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs">
            <Switch checked={quiz.scored || false} onCheckedChange={(v) => patchQuiz({ scored: v })} /> Scored
          </label>
          <label className="inline-flex items-center gap-2 text-xs">
            <Switch checked={quiz.shuffle_questions || false} onCheckedChange={(v) => patchQuiz({ shuffle_questions: v })} /> Shuffle
          </label>
        </aside>

        <div className="space-y-3">
          <div className="border-b border-border flex gap-1">
            {[["questions", `Questions (${questions.length})`], ["links", "Share links"], ["analytics", "Analytics"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === v ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid={`stdq-tab-${v}`}>{l}</button>
            ))}
          </div>

          {tab === "questions" && (
            <div className="space-y-2">
              {questions.map((q, i) => (
                <QuestionRow key={q.id} q={q} idx={i} onPatch={(body) => patchQuestion(q.id, body)} onDelete={() => deleteQuestion(q.id)} />
              ))}
              <Button variant="outline" className="rounded-none w-full h-8" onClick={addQuestion} data-testid="stdq-add-question">
                <Plus className="w-3 h-3 mr-1" /> Add question
              </Button>
            </div>
          )}

          {tab === "links" && (
            <div className="space-y-2">
              <Button className="rounded-none" onClick={() => setLinkOpen(true)} data-testid="stdq-new-link">
                <Link2 className="w-4 h-4 mr-1" /> New share link
              </Button>
              {links.length === 0 && <div className="text-sm text-muted-foreground italic">No share links yet.</div>}
              {links.map((l) => (
                <div key={l.id} className="border border-border p-3" data-testid={`stdq-link-${l.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`chip text-[9px] ${l.kind === "invited" ? "chip-warning" : "chip-info"}`}>{l.kind.toUpperCase()}</span>
                    {l.require_otp && <span className="chip chip-danger text-[9px]">OTP</span>}
                    <span className="text-sm font-semibold flex-1">{l.label || "(unlabelled)"}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {l.expires_at ? `expires ${l.expires_at.split("T")[0]}` : "no expiry"}
                    </span>
                    <button onClick={() => copyLink(l.token)} className="text-primary" title="Copy public URL"><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => revokeLink(l.id)} className="text-rose-600" title="Revoke"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1 truncate">/pq/{l.token}</div>
                  {l.kind === "invited" && (
                    <div className="mt-2 text-xs">
                      <div className="label-eyebrow mb-1">Invitees ({(l.invitees || []).length})</div>
                      <div className="space-y-0.5 max-h-40 overflow-y-auto">
                        {(l.invitees || []).map((inv) => (
                          <div key={inv.id} className="flex items-center gap-2 text-[11px] py-0.5">
                            <span className="flex-1 truncate">{inv.email}</span>
                            {inv.submitted_at ? <span className="chip chip-success text-[9px]">SUBMITTED</span>
                              : inv.opened_at ? <span className="chip chip-info text-[9px]">OPENED</span>
                              : <span className="chip text-[9px]">PENDING</span>}
                            <button onClick={() => copyLink(inv.token)} className="text-primary" title="Copy invitee URL"><Copy className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "analytics" && (
            <div className="space-y-3">
              {!analytics && <div className="text-sm text-muted-foreground">Loading…</div>}
              {analytics && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <StatCard label="Attempts" value={analytics.attempts} />
                    <StatCard label="Pass rate" value={`${analytics.pass_rate}%`} />
                    <StatCard label="Avg score" value={analytics.avg_score_percent != null ? `${analytics.avg_score_percent}%` : "—"} />
                    <StatCard label="Auto-submitted" value={analytics.auto_submitted} accent={analytics.auto_submitted > 0 ? "warning" : ""} />
                  </div>
                  {Object.keys(analytics.violations_by_kind || {}).length > 0 && (
                    <div className="border border-border p-3">
                      <div className="text-[10px] label-eyebrow mb-2">Violations by kind</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {Object.entries(analytics.violations_by_kind).map(([k, v]) => (
                          <span key={k} className="chip chip-danger text-[10px]">{k}: {v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="border border-border p-3">
                    <div className="text-[10px] label-eyebrow mb-2">Hardest questions (highest failure rate)</div>
                    <div className="space-y-1">
                      {(analytics.hardest_questions || []).slice(0, 5).map((q) => (
                        <div key={q.id} className="text-xs border-l-2 border-rose-500 pl-2">
                          <div className="font-semibold">{q.text}</div>
                          <div className="text-muted-foreground text-[11px]">
                            failure rate: {q.failure_rate}% · {q.wrong} wrong / {q.attempts} attempts
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="stdq-link-dialog">
          <DialogHeader><DialogTitle>Create share link</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Kind</Label>
              <Select value={linkForm.kind} onValueChange={(v) => setLinkForm({ ...linkForm, kind: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="stdq-link-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single tokenised link (unlimited takers)</SelectItem>
                  <SelectItem value="invited">Invited (per-invitee unique link)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Label</Label>
              <Input value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="e.g. Q1 external hiring" className="h-9 rounded-none" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Expires in (days)</Label>
              <Input type="number" min={1} max={365} value={linkForm.expires_in_days} onChange={(e) => setLinkForm({ ...linkForm, expires_in_days: e.target.value })} className="h-9 rounded-none" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={linkForm.require_otp} onCheckedChange={(v) => setLinkForm({ ...linkForm, require_otp: v })} data-testid="stdq-link-otp" />
              Require email OTP before opening
            </label>
            {linkForm.kind === "invited" && (
              <div>
                <Label className="text-xs mb-1 block">Invitee emails (comma/space separated)</Label>
                <Textarea value={linkForm.invitee_emails} onChange={(e) => setLinkForm({ ...linkForm, invitee_emails: e.target.value })} rows={3} className="rounded-none" placeholder="jane@example.com, john@example.com" data-testid="stdq-link-emails" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={createShareLink} data-testid="stdq-link-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const bg = accent === "warning" ? "bg-amber-50 dark:bg-amber-900/20" : "";
  return (
    <div className={`border border-border p-3 ${bg}`}>
      <div className="text-[10px] label-eyebrow">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function QuestionRow({ q, idx, onPatch, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border" data-testid={`stdq-q-${q.id}`}>
      <div className="flex items-center gap-2 p-2">
        <span className="text-[10px] text-muted-foreground font-mono">#{idx + 1}</span>
        <Select value={q.type} onValueChange={(v) => onPatch({ ...q, type: v })}>
          <SelectTrigger className="h-7 w-32 rounded-none text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{QTYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input defaultValue={q.text} onBlur={(e) => e.target.value !== q.text && onPatch({ ...q, text: e.target.value })} className="h-7 rounded-none text-sm flex-1" />
        <span className="text-[10px] text-muted-foreground">{q.points || 1}pt</span>
        <button onClick={() => setExpanded(!expanded)} className="text-primary text-[11px]">{expanded ? "Hide" : "Edit"}</button>
        <button onClick={onDelete} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {expanded && (
        <div className="p-2 border-t border-border space-y-2">
          {(q.type === "mcq" || q.type === "multi") && (
            <div className="space-y-1">
              <div className="text-[10px] label-eyebrow">Options + correct answers</div>
              {(q.options || []).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="checkbox" checked={(q.correct_answers || []).includes(o.key)} onChange={(e) => {
                    let ca = q.correct_answers || [];
                    if (q.type === "mcq") ca = e.target.checked ? [o.key] : [];
                    else ca = e.target.checked ? [...ca, o.key] : ca.filter((x) => x !== o.key);
                    onPatch({ ...q, correct_answers: ca });
                  }} />
                  <Input defaultValue={o.label} onBlur={(e) => {
                    const opts = [...(q.options || [])];
                    opts[i] = { ...opts[i], label: e.target.value };
                    onPatch({ ...q, options: opts });
                  }} className="h-7 rounded-none text-xs flex-1" />
                  <button onClick={() => onPatch({ ...q, options: (q.options || []).filter((_, x) => x !== i), correct_answers: (q.correct_answers || []).filter((k) => k !== o.key) })} className="text-rose-600"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-none h-6 text-[11px]" onClick={() => {
                const nextKey = String.fromCharCode(65 + (q.options || []).length);
                onPatch({ ...q, options: [...(q.options || []), { key: nextKey, label: `Option ${nextKey}` }] });
              }}>+ Option</Button>
            </div>
          )}
          {q.type === "truefalse" && (
            <Select value={(q.correct_answers || [])[0] || "T"} onValueChange={(v) => onPatch({ ...q, correct_answers: [v], options: [{ key: "T", label: "True" }, { key: "F", label: "False" }] })}>
              <SelectTrigger className="h-7 w-40 rounded-none text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="T">True is correct</SelectItem>
                <SelectItem value="F">False is correct</SelectItem>
              </SelectContent>
            </Select>
          )}
          {q.type === "short_text" && (
            <div>
              <Label className="text-[10px] label-eyebrow">Expected answer (exact match, case-insensitive)</Label>
              <Input defaultValue={q.correct_text || ""} onBlur={(e) => e.target.value !== (q.correct_text || "") && onPatch({ ...q, correct_text: e.target.value })} className="h-7 rounded-none text-xs" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] label-eyebrow">Points</Label>
              <Input type="number" min={0} max={100} defaultValue={q.points || 1} onBlur={(e) => Number(e.target.value) !== (q.points || 1) && onPatch({ ...q, points: Number(e.target.value) })} className="h-7 rounded-none text-xs" />
            </div>
            <div>
              <Label className="text-[10px] label-eyebrow">Explanation</Label>
              <Input defaultValue={q.explanation || ""} onBlur={(e) => e.target.value !== (q.explanation || "") && onPatch({ ...q, explanation: e.target.value })} className="h-7 rounded-none text-xs" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
